import React from 'react'
import {
  AspectRatio,
  Box,
  BoxProps,
  Button,
  ButtonProps,
  Container,
  Flex,
  HStack,
  Text,
  VStack
} from '@chakra-ui/react'
import { useQuery } from '@tanstack/react-query'
import Image from 'next/image'

import Head from '@modules/common/components/head'
import BrandLayout from '@layouts/brand-layout'
import { getBrand } from '@lib/util/brand-handler'

import Certification from './certification'
import ClinicallyResult from './clinically-result'
import KeyIngredient from './key-ingredient'
import Review from './review'
import HowToUse from './how-to-use'
import Foundation from './foundation'
import Order from './order'
import PercentRating from './percent-rating'
import Videos from './videos'
import WhereToBuy from './where-to-buy'
import QA from './qa'
import Link from 'next/link'
import { ChevronRightIcon } from '@chakra-ui/icons'
import useAosAnimation from '@common/hooks/use-aos-animation'

const dataImages = {
  hero: 'https://habbit-sg.s3.ap-southeast-1.amazonaws.com/hero-1711083831404.png',
  allInOne:
    'https://habbit-sg.s3.ap-southeast-1.amazonaws.com/all-in-one-1710993056924.png'
}

export const CONTAINER_WIDTH = {
  LARGE: 800
}

const MainContainer = (props: BoxProps) => {
  return <Container maxW={CONTAINER_WIDTH.LARGE} {...props} />
}

const ButtonBuy = ({ children, ...props }: ButtonProps) => {
  return (
    <Link
      href="/products/doctorcos-snow-white-water-glow-mask"
      style={{ display: 'contents' }}
    >
      <Button
        fontFamily="Inter"
        variant="ghost"
        textTransform="uppercase"
        fontWeight={400}
        background="white"
        color="#17B6B1"
        border="4px solid #17B6B1"
        borderWidth={{
          base: '2px',
          md: '4px'
        }}
        borderRadius={40}
        fontSize={{
          base: 10,
          sm: 14,
          md: 20
        }}
        ml={{
          base: 1,
          md: 4
        }}
        pl={{
          base: 2,
          md: 4
        }}
        pr={0}
        h={{
          base: 5,
          sm: 8,
          md: 12
        }}
        {...props}
      >
        <HStack gap={0} justifyContent={'center'}>
          <Box>{children}</Box>
          <ChevronRightIcon
            w={{
              base: 4,
              sm: 6,
              md: 8
            }}
            h={{ base: 4, sm: 6, md: 8 }}
            color="red.500"
          />
        </HStack>
      </Button>
    </Link>
  )
}

const Doctorocs = () => {
  const { data } = useQuery(['brand', 'ellixi'], () => getBrand('ellixi'))

  useAosAnimation({
    duration: 600,
    once: false
  })

  if (!data) return null

  const { title, metadata, productSection, color } = data

  return (
    <>
      <Head title={title} description={metadata.meta_description} />
      <BrandLayout
        name={title}
        color={color || '#E31C5A'}
        brand={data}
        productSection={productSection}
      >
        <Box
          bgColor="#FCFCFC"
          mt={{
            base: '60px',
            md: '120px'
          }}
        >
          <MainContainer data-aos="fade-up">
            <VStack w="100%" alignItems="center">
              <AspectRatio
                w={{
                  base: '100%',
                  md: '800px'
                }}
                ratio={800 / 815}
                position="relative"
              >
                <Box>
                  <Image src={dataImages.hero} alt="Hero" fill />
                  <ButtonBuy
                    position="absolute"
                    top="52%"
                    left="10px"
                    zIndex={10}
                  >
                    Buy Now
                  </ButtonBuy>
                </Box>
              </AspectRatio>

              <AspectRatio
                w={{
                  base: '100%',
                  md: '800px'
                }}
                ratio={800 / 575}
                position="relative"
              >
                <Image src={dataImages.allInOne} alt="All in one" fill />
              </AspectRatio>
            </VStack>
          </MainContainer>

          <MainContainer data-aos="fade-up">
            <Flex flexDir={{ base: 'column', md: 'row' }} alignItems="center">
              <VStack
                flex={1}
                ml={{
                  base: 0,
                  md: 14
                }}
                alignItems={{ base: 'center', md: 'flex-start' }}
                gap={0}
              >
                <AspectRatio w="80px" ratio={1 / 1} position="relative">
                  <Image
                    src="https://habbit-sg.s3.ap-southeast-1.amazonaws.com/flag-korea-1711001370498.png"
                    alt="korea flag"
                    fill
                  />
                </AspectRatio>
                <Text
                  textStyle="docMainTitle"
                  textTransform="uppercase"
                  textAlign="left"
                >
                  SNOW WHITE <br />
                  WATER GLOW MASK
                </Text>

                <Text
                  textStyle="docSubTitle"
                  textTransform="uppercase"
                  fontWeight={500}
                  textAlign="left"
                >
                  OVER 8M UNITS SOLD
                </Text>
                <Text
                  textStyle="docInfo"
                  textTransform="uppercase"
                  textAlign="left"
                >
                  ALL-IN-ONE SHEET-FREE FACIAL MASK
                </Text>
              </VStack>

              <AspectRatio
                w={{
                  base: '100%',
                  sm: '500px',
                  md: '400px'
                }}
                ratio={400 / 265}
                position="relative"
              >
                <Image
                  src="https://habbit-sg.s3.ap-southeast-1.amazonaws.com/doctorcos-sale-1710994611110.png"
                  alt="doctorcos sale"
                  fill
                />
              </AspectRatio>
            </Flex>
          </MainContainer>

          <MainContainer
            data-aos="fade-up"
            py={{
              md: 20,
              base: 10
            }}
          >
            <iframe
              width="100%"
              height="400"
              src="https://www.youtube.com/embed/59AxVmrqYwU?si=ff94RUkORBHIBSoK"
              title="YouTube video player"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
            ></iframe>
          </MainContainer>

          <MainContainer
            data-aos="fade-up"
            py={{
              md: 20,
              base: 10
            }}
          >
            <WhereToBuy />
          </MainContainer>

          <Box bgColor="#E6CD7E" data-aos="fade-up">
            <MainContainer>
              <Flex
                flexDir={{ base: 'column', md: 'row' }}
                alignItems="flex-start"
              >
                <VStack
                  flex={1}
                  justifyItems="center"
                  alignItems="center"
                  w="100%"
                >
                  <AspectRatio
                    w={{
                      base: '100%',
                      sm: '80%',
                      md: '440px'
                    }}
                    ratio={1 / 1}
                    position="relative"
                  >
                    <Image
                      src="https://habbit-sg.s3.ap-southeast-1.amazonaws.com/people-skin-1710995071878.png"
                      alt="people skin"
                      fill
                    />
                  </AspectRatio>
                </VStack>

                <VStack
                  flex={1}
                  alignItems={{
                    base: 'center',
                    md: 'flex-start'
                  }}
                  mt={10}
                  ml={{
                    base: 0,
                    md: -20
                  }}
                  p={0}
                >
                  <AspectRatio
                    ml={{
                      base: '8px',
                      md: '70px'
                    }}
                    w={240}
                    ratio={240 / 88}
                    position="relative"
                  >
                    <Image
                      src="https://habbit-sg.s3.ap-southeast-1.amazonaws.com/air-conditioner-1711001289678.png"
                      alt="air conditioner"
                      fill
                    />
                  </AspectRatio>

                  <Text
                    textTransform="uppercase"
                    textStyle="docMainTitle"
                    textAlign="left"
                    mt={1}
                  >
                    Need A Deep Moisturizer <br />
                    For Yours Skin?
                  </Text>

                  <Text textStyle="docInfo" textAlign="left" mt={1}>
                    Especially for those who work in air-conditioned
                    environments. Doctorcos Snow White Glow Mask is an all in
                    one water glow mask that moisturizes and strengthens the
                    elasticity of your skin, as well as providing nutrition and
                    a whitening effect.
                  </Text>
                  <Box
                    display="flex"
                    justifyContent="flex-start"
                    gap={2}
                    mb={{
                      base: 4,
                      md: 0
                    }}
                  >
                    <AspectRatio
                      w={{
                        base: '100px',
                        md: '120px'
                      }}
                      ratio={100 / 67}
                      position="relative"
                    >
                      <Image
                        src="https://habbit-sg.s3.ap-southeast-1.amazonaws.com/skin-01-1711026899755.png"
                        alt="skin"
                        style={{
                          border: '4px solid #fff'
                        }}
                        fill
                      />
                    </AspectRatio>

                    <AspectRatio
                      w={{
                        base: '100px',
                        md: '120px'
                      }}
                      ratio={100 / 67}
                      position="relative"
                    >
                      <Image
                        src="https://habbit-sg.s3.ap-southeast-1.amazonaws.com/skin-02-1711026899757.png"
                        alt="skin"
                        style={{
                          border: '4px solid #fff'
                        }}
                        fill
                      />
                    </AspectRatio>

                    <AspectRatio
                      w={{
                        base: '100px',
                        md: '120px'
                      }}
                      ratio={100 / 67}
                      position="relative"
                    >
                      <Image
                        src="https://habbit-sg.s3.ap-southeast-1.amazonaws.com/skin-03-1711026899757.png"
                        alt="skin"
                        style={{
                          border: '4px solid #fff'
                        }}
                        fill
                      />
                    </AspectRatio>
                  </Box>
                </VStack>
              </Flex>
            </MainContainer>
          </Box>

          <MainContainer data-aos="fade-up">
            <Flex
              flexDir={{ base: 'column', md: 'row' }}
              alignItems="flex-start"
            >
              <VStack
                flex={1}
                alignItems={{
                  base: 'center',
                  md: 'flex-start'
                }}
                mt={10}
                p={0}
                w="100%"
              >
                <Text
                  textStyle="docMainTitle"
                  textTransform="uppercase"
                  textAlign={{
                    base: 'center',
                    md: 'left'
                  }}
                  w={360}
                  mt={1}
                  ml={{
                    base: 0,
                    md: 5
                  }}
                >
                  ULTIMATE HYDRATION AND RICH NOURISHMENT
                </Text>

                <Text
                  textStyle="docInfo"
                  textAlign="left"
                  mt={1}
                  ml={{
                    base: 0,
                    md: 10
                  }}
                >
                  ✓ Up to 120 hours moisturizing effect <br />
                  ✓ Helps to improve eye wrinkles <br />
                  ✓ Improvement effect of dermis tightness <br />
                  ✓ Skin pores improvement <br />
                  ✓ Improve skin elasticity and moisture <br />
                  ✓ Reduce skin temperature and control sebum <br />
                  ✓ Natural ingredients, fragrance free <br />
                </Text>
              </VStack>
              <VStack
                flex={1}
                justifyItems="center"
                alignItems="center"
                w="100%"
                mt={{
                  base: 4,
                  md: 0
                }}
                ml={{
                  base: 0,
                  md: -20
                }}
              >
                <AspectRatio w={440} ratio={500 / 350} position="relative">
                  <Image
                    src="https://habbit-sg.s3.ap-southeast-1.amazonaws.com/beautiful-skin-1710995607459.png"
                    alt="beauti skin"
                    fill
                  />
                </AspectRatio>
              </VStack>
            </Flex>

            <Box w="100%" mt="1px" h={1} bgColor="#E7CD7E"></Box>
          </MainContainer>

          <MainContainer>
            <VStack alignItems="center">
              <PercentRating />
              <Certification />
              <ClinicallyResult />
            </VStack>
          </MainContainer>

          <Box
            data-aos="fade-up"
            bgImage={`url(https://habbit-sg.s3.ap-southeast-1.amazonaws.com/bg-key-ingredient-1711001605928.png)`}
            w="100%"
            bgSize="cover"
          >
            <MainContainer justifyContent="center">
              <KeyIngredient />
            </MainContainer>
          </Box>

          <MainContainer data-aos="fade-up" justifyContent="center">
            <Videos />
          </MainContainer>

          <Box
            data-aos="fade-up"
            mt={5}
            bgImage={`url(https://habbit-sg.s3.ap-southeast-1.amazonaws.com/bg-review-1711001682887.jpeg)`}
            bgSize="cover"
          >
            <MainContainer display="flex" justifyContent="center">
              <Review />
            </MainContainer>
          </Box>

          <MainContainer data-aos="fade-up">
            <VStack alignItems="center">
              <HowToUse />
              <Foundation />
              <Order />
            </VStack>
          </MainContainer>

          <Box
            data-aos="fade-up"
            bgImage={`url(https://habbit-sg.s3.ap-southeast-1.amazonaws.com/bg-qa-1711001682884.png)`}
            bgSize="cover"
          >
            <MainContainer justifyContent="center">
              <QA />
            </MainContainer>
          </Box>
        </Box>
      </BrandLayout>
    </>
  )
}

export default Doctorocs
